<template>
  <div id="four-o-four-view">
    <div class="error-page">
      <!-- Message -->
      <div class="error-content">
        <p class="error-code">404</p>
        <h1 class="error-title">This page doesn’t exist</h1>
        <p class="error-message">
          The link may have expired, or the address might have a typo. Nothing is
          wrong with your account — pick a destination below to carry on.
        </p>
        <router-link
          v-if="isSignedIn"
          :to="{ name: 'OverviewDetails' }"
          class="primary-action"
        >
          Back to dashboard
        </router-link>
        <router-link v-else :to="{ name: 'SignIn' }" class="primary-action">
          Sign in
        </router-link>
      </div>

      <!-- Destinations -->
      <nav class="links-card" aria-label="Suggested pages">
        <p class="links-title">{{ isSignedIn ? "Go to" : "Explore" }}</p>
        <ul class="links-list">
          <li v-for="link in links" :key="link.title">
            <router-link v-if="link.to" :to="link.to" class="link-row">
              <span>{{ link.title }}</span>
              <svg class="chevron" viewBox="0 0 16 16" aria-hidden="true">
                <path
                  d="M6 3.5L10.5 8L6 12.5"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </router-link>
            <a
              v-else
              :href="link.href"
              rel="noopener noreferrer"
              class="link-row"
            >
              <span>{{ link.title }}</span>
              <svg class="chevron" viewBox="0 0 16 16" aria-hidden="true">
                <path
                  d="M6 3.5L10.5 8L6 12.5"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </a>
          </li>
        </ul>
      </nav>
    </div>
  </div>
</template>

<script>
  import { getItem } from "@/utils/storage.js";

  export default {
    name: "404",

    data() {
      return {
        isSignedIn: false,
      };
    },

    computed: {
      // Every signed-in entry is a route this app actually defines — a
      // router-link to a missing route throws on render, which is what the
      // previous "Transactions" entry did. Investor-only destinations
      // (Exchange, MyInvestments, GiftEquity) have no route here and are
      // replaced by the sponsor equivalents.
      links() {
        if (this.isSignedIn) {
          return [
            { title: "Deals", to: { name: "Deals" } },
            { title: "Invest", to: { name: "Invest" } },
            { title: "Wallet", to: { name: "Wallet" } },
            { title: "Members", to: { name: "Members" } },
            { title: "Teams", to: { name: "Teams" } },
            { title: "API client", to: { name: "ApiClient" } },
            { title: "Account settings", to: { name: "Account" } },
          ];
        }
        return [
          { title: "GetEquity home", href: "https://www.getequity.io/" },
          { title: "Crowdraise", href: "https://www.getequity.io/crowdraise" },
          { title: "Investor app", href: "https://app.getequity.io/" },
          { title: "Syndicate", href: "https://dealroom.getequity.io/syndicate" },
          { title: "Communities", href: "https://dealroom.getequity.io/community" },
        ];
      },
    },

    mounted() {
      this.isSignedIn = Boolean(getItem("org_token"));
    },
  };
</script>

<style lang="scss" scoped>
  #four-o-four-view {
    min-height: 100vh;
    background: #f8fafc;
  }

  .error-page {
    max-width: 1280px;
    margin: 0 auto;
    padding: 96px 24px 64px;
    display: grid;
    grid-template-columns: 1fr;
    gap: 48px;
    align-items: start;

    // Message column gets the room; the link card stays a fixed rail.
    @media (min-width: 1024px) {
      grid-template-columns: minmax(0, 1fr) 340px;
      gap: 64px;
      padding: 120px 24px 80px;
    }

    @media (max-width: 640px) {
      padding: 64px 16px 48px;
      gap: 32px;
    }
  }

  // ── Message ────────────────────────────────────────────────────────────────
  .error-code {
    font-size: 72px;
    font-weight: 600;
    line-height: 1;
    letter-spacing: -2px;
    color: #001240;
    margin-bottom: 24px;

    @media (min-width: 1024px) {
      font-size: 88px;
    }
  }

  .error-title {
    font-size: 28px;
    font-weight: 500;
    line-height: 1.2;
    color: #0f172a;
    margin-bottom: 12px;
  }

  .error-message {
    font-size: 16px;
    font-weight: 400;
    line-height: 1.6;
    color: #64748b;
    max-width: 52ch;
    margin-bottom: 32px;
  }

  .primary-action {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 44px;
    padding: 0 24px;
    background: #001240;
    color: #ffffff;
    font-size: 14px;
    font-weight: 500;
    border-radius: 7px;
    transition: background-color 150ms ease;

    &:hover {
      background: #06206b;
    }

    &:focus-visible {
      outline: 2px solid #4d84ff;
      outline-offset: 2px;
    }

    &:active {
      background: #001240;
    }
  }

  // ── Destinations ───────────────────────────────────────────────────────────
  .links-card {
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 7px;
    padding: 8px;
  }

  .links-title {
    font-size: 13px;
    font-weight: 500;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    padding: 12px 12px 8px;
  }

  .links-list {
    list-style: none;

    li + li {
      border-top: 1px solid #f1f5f9;
    }
  }

  .link-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 12px;
    border-radius: 4px;
    font-size: 14px;
    font-weight: 400;
    color: #0f172a;
    transition: background-color 150ms ease, color 150ms ease;

    .chevron {
      width: 16px;
      height: 16px;
      flex-shrink: 0;
      color: #94a3b8;
      transition: color 150ms ease, transform 150ms ease-out;
    }

    &:hover {
      background: #f8fafc;
      color: #4d84ff;

      .chevron {
        color: #4d84ff;
        transform: translateX(2px);
      }
    }

    &:focus-visible {
      outline: 2px solid #4d84ff;
      outline-offset: -2px;
    }

    &:active {
      background: #f1f5f9;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .primary-action,
    .link-row,
    .link-row .chevron {
      transition: none;
    }

    .link-row:hover .chevron {
      transform: none;
    }
  }
</style>
